import { getIntl } from './intl';
import { AuditLogTypes } from '../primary-record-types/AuditLogs';
import { MedicalDataTypes } from '../primary-record-types';

export const resourceCategoryMessages = getIntl('shared-messages-resource-categories', {
    condition: 'Condition',
    medication: 'Medication',
    allergy: 'Allergy',
    visit: 'Visit',
    vaccination: 'Vaccination',
    procedure: 'Procedure',
    imaging: 'Imaging',
    lab: 'Lab',
});

export const pluralResourceCategoryMessages = getIntl('shared-messages-resource-plural-categories', {
    condition: 'Conditions',
    medication: 'Medications',
    allergy: 'Allergies',
    visit: 'Visits',
    vaccination: 'Vaccinations',
    procedure: 'Procedures',
    imaging: 'Imaging',
    lab: 'Labs',
});

export const conditionStatusMessages = getIntl('shared-messages-condition-status', {
    active: 'Active',
    inactive: 'Inactive',
    chronic: 'Chronic',
    'ruled-out': 'Ruled Out',
});

export const allergyStatusMessages = getIntl('shared-messages-allergy-status', {
    active: 'Active',
    inactive: 'Inactive',
    chronic: 'Chronic',
    'ruled-out': 'Ruled Out',
});

export const allergyTypeMessages = getIntl('shared-messages-allergy-type', {
    food: 'Food',
    drug: 'Medication',
    environment: 'Environmental',
});

export const medicationFrequency = getIntl('shared-messages-medication-frequency', {
    daily: 'Daily',
    '2-daily': 'Twice Daily',
    '3-daily': 'Three Times Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    continuously: 'Continuously',
    other: 'Other',
});

export const visitTypeMessages = getIntl('shared-messages-visit-type', {
    routine: 'Routine',
    'walk-in': 'Walk-In',
    checkup: 'Checkup',
    followup: 'Followup',
    emergency: 'Emergency',
    other: 'Other',
});

/** Display text for a visit's type — a custom "Other" label replaces the generic type name. */
export const getVisitTypeDisplay = (visit: {
    visitType: MedicalDataTypes.PrimaryRecordVisitType | null;
    visitTypeOtherLabel?: string | null;
}): string | null => {
    if (visit.visitType === 'other' && visit.visitTypeOtherLabel) return visit.visitTypeOtherLabel;
    return visit.visitType ? visitTypeMessages(visit.visitType) : null;
};

export const visitDurationMessages = getIntl('shared-messages-visit-duration', {
    15: '15 Min',
    30: '30 Min',
    45: '45 Min',
    60: '1 Hour',
    90: '90 Min',
    120: '2 Hours',
});

export const noteCategoryTagMessages = getIntl('shared-messages-note-category-tag', {
    'care-plan': 'Care Plan',
    financial: 'Financial',
    legal: 'Legal',
    research: 'Research',
    routines: 'Routines',
    supplies: 'Supplies',
    symptom: 'Symptoms',
    intake: 'Intake',
    visit: 'Visit',
    observation: 'Observation',
    question: 'Question',
    instructions: 'Instructions',
    task: 'Task',
    reminder: 'Reminder',
    issue: 'Issue',
    'client-preference': 'Client preference',
    'change-in-client': 'Change in client',
    assessment: 'Assessment',
});

export const noteEntityTypes = getIntl('shared-messages-note-entity-types', {
    condition: 'Conditions',
    medication: 'Medications',
    allergy: 'Allergies',
    visit: 'Visits',
    vaccination: 'Vaccinations',
    procedure: 'Procedures',
    imaging: 'Imaging',
    'medical-profile': 'Medical Profile',
    'care-team-member': 'Care Team Member',
    'care-team': 'Care Team',
    resource: 'Resources',
    user: 'User',
    lab: 'Labs',
    'condition-resource': 'Condition',
    'medication-resource': 'Medication',
    'allergy-resource': 'Allergy',
    'visit-resource': 'Visit',
    'procedure-resource': 'Procedure',
    'vaccination-resource': 'Vaccination',
    'imaging-resource': 'Imaging',
    'lab-resource': 'Lab',
    'organization-client-record': 'Client Record',
    organization: 'Organization',
});

export const entitlements = getIntl('shared-messages-entitlements', {
    'Family Plan': 'Family Plan',
    'Individual Plan': 'Individual Plan',
    'Setup and Training': 'Setup and Training',
});

export const products = getIntl('shared-messages-products', {
    'setup-and-training': 'Setup and Training',
    'individual-recurring': 'Individual Plan',
    'family-recurring': 'Family Plan',
    'family-lifetime': 'Family Lifetime Plan',
});

export const reportTypeMessages = getIntl('shared-messages-report-types', {
    'all-medications': 'Active and Inactive Medications List',
    'active-medications': 'Active Medications List',
    'summary-report': 'Summary Report',
});

export const auditLogDescriptors: Record<AuditLogTypes, string> = {
    'user-profile-view': 'Profile Viewed',
    'medical-profile-creation': 'Medical Profile Created',
    'medical-profile-deletion': 'Medical Profile Deleted',
    'collaborator-add': 'Collaborator Added',
    'collaborator-remove': 'Collaborator Removed',
    'collaborator-invite': 'Collaborator Invited',
    'collaborator-invite-accept': 'Collaborator Invite Accepted',
    'collaborator-invite-decline': 'Collaborator Invite Declined',
    'care-team-member-collaborator-remove': 'Care Team Member Collaborator Removed',
    'care-team-member-collaborator-add': 'Care Team Member Collaborator Added',
    'resource-creation': 'Resource Created',
    'resource-update': 'Resource Updated',
    'resource-delete': 'Resource Deleted',
    'no-known-allergies-indicated': 'No Known Allergies Indicated',
    'note-creation': 'Note Created',
    'note-move': 'Note Moved',
    'note-comment': 'Note Comment',
    '1up-integration-add': '1up Integration Added',
    '1up-integration-refresh': '1up Integration Refreshed',
    '1up-integration-remove': '1up Integration Removed',
    'fasten-integration-add': 'Fasten Integration Added',
    'fasten-integration-refresh': 'Fasten Integration Refreshed',
    'fasten-integration-remove': 'Fasten Integration Removed',
    'medical-profile-ownership-transfer': 'Medical Profile Ownership Transferred',
    'shared-report-creation': 'Shared Report Created',
    'shared-report-deletion': 'Shared Report Deleted',
    'shared-report-viewed': 'Shared Report Viewed',
    'user-registration': 'User Registered',
    'portal-refresh-completion': 'Portal Refresh Completed',
    'profile-message': 'Profile Message',
    'organization-collaborator-add': 'Organization Collaborator Added',
    'organization-collaborator-remove': 'Organization Collaborator Removed',
    'organization-publicly-visible-status-update': 'Organization Publicly Visible Status Updated',
    'organization-session-timeout-update': 'Organization Session Timeout Updated',
    'organization-paid-status-update': 'Organization Paid Status Updated',
    'organization-user-creation': 'Organization User Created',
    'organization-user-creation-on-invite-accept': 'Organization User Created on Invite Accept',
    'organization-owner-role-assignment': 'Organization Owner Role Assigned',
    'organization-user-role-assignment': 'Organization User Role Assigned',
    'organization-user-update': 'Organization User Updated',
    'organization-user-delete': 'Organization User Deleted',
    'organization-user-group-add': 'Organization User Group Added',
    'organization-user-group-remove': 'Organization User Group Removed',
    'organization-user-group-add-bulk': 'Organization User Groups Added (Bulk)',
    'organization-user-group-remove-bulk': 'Organization User Groups Removed (Bulk)',
    'organization-care-team-member-add': 'Organization Care Team Member Added',
    'organization-care-team-member-update': 'Organization Care Team Member Updated',
    'organization-care-team-member-delete': 'Organization Care Team Member Deleted',
    'organization-note-creation': 'Organization Note Created',
    'organization-note-update': 'Organization Note Updated',
    'organization-note-delete': 'Organization Note Deleted',
    'medical-profile-file-upload': 'File Uploaded',
    'medical-profile-file-delete': 'File Deleted',
    'organization-invoice-creation': 'Organization Invoice Created',
    'code-status-directive-creation': 'Code Status Directive Created',
    'code-status-directive-update': 'Code Status Directive Updated',
    'poa-directive-creation': 'POA Directive Created',
    'poa-directive-update': 'POA Directive Updated',
};

export const auditLogTypeMessages = getIntl('shared-messages-audit-log-types', auditLogDescriptors);

export const reactionTypeMessages = getIntl('shared-messages-reaction-types', {
    allergic: 'Allergy',
    intolerant: 'Intolerance',
    avoidance: 'Avoidance',
});

export const reactionTypeDescription = getIntl('shared-messages-reaction-description', {
    allergic: 'This causes a rash, shortness of breath or swelling',
    intolerant: 'This gives me a headache or nausea',
    avoidance: 'Avoid due to a diagnosis or medication interaction',
});

const friendlyExtensionMessages = {
    '.dcm': 'Digital Imaging and Communications in Medicine',
    '.nii': 'Neuroimaging',
} as const;

const contentTypesWithFriendlyNames = Object.keys(friendlyExtensionMessages);

const friendlyContentTypeIntlMessages = getIntl('shared-messages-friendly-content-types', friendlyExtensionMessages);

export const getFriendlyContentType = (extension: string, contentType: string) => {
    if (contentTypesWithFriendlyNames.includes(extension)) {
        return friendlyContentTypeIntlMessages(extension as any);
    } else {
        return contentType;
    }
};

export const formTitles = {
    edit: getIntl('shared-messages-form-titles-edit', {
        condition: 'Edit Condition',
        medication: 'Edit Medication',
        allergy: 'Edit Allergy',
        visit: 'Edit Visit',
        vaccination: 'Edit Vaccination',
        procedure: 'Edit Procedure',
        imaging: 'Edit Imaging',
        lab: 'Edit Lab',
    }),
    create: getIntl('shared-messages-form-titles-create', {
        condition: 'Add Condition',
        medication: 'Add Medication',
        allergy: 'Add Allergy',
        visit: 'Add Visit',
        vaccination: 'Add Vaccination',
        procedure: 'Add Procedure',
        imaging: 'Add Imaging',
        lab: 'Add Lab',
    }),
};

export const organizationRoles = getIntl('shared-messages-organization-roles', {
    admin: 'Administrator',
    agent: 'Agent',
    owner: 'Organization Owner',
});

export const clientRecordStatus = getIntl('shared-messages-client-record-status', {
    'not-invited': 'Not Invited',
    invited: 'Invite Sent',
    'invitation-rejected': 'Invitation Rejected',
    active: 'Active',
    inactive: 'Inactive',
    archived: 'Archived',
});

export const careTeamType = getIntl('shared-messages-care-team-type', {
    support: 'Support',
    provider: 'Provider',
    insurance: 'Insurance',
});

export const codeStatusTypeMessages = getIntl('shared-messages-code-status-type', {
    'full-code': 'Full Code',
    'partial-code-dni': 'Partial Code (DNI)',
    'partial-code-other': 'Partial Code (Other)',
    dnr: 'DNR',
    'comfort-measures': 'Comfort Measures',
});

export const poaRelationshipTypeMessages = getIntl('shared-messages-code-status-type', {
    family: 'Family',
    friend: 'Friend',
    'professional-advisor': 'Professional Advisor',
    trustee: 'Trustee',
});
